import { memo, useEffect, useRef } from 'react';
import type { PenType, Stroke, StrokePoint, StrokesBlock } from '../types/models';
import { A4_LOGICAL_WIDTH, PAGE_MIN_HEIGHT } from '../utils/answers';
import { drawStroke } from '../utils/inkRender';
import { shapeGeometry, type ShapeKind } from '../utils/shapes';

export interface InkTool {
  tool: 'pen' | 'eraser';
  color: string;
  width: number;
  eraserSize: number;
  // The pen nib (ballpoint/gel/fountain/marker). Only meaningful while tool === 'pen'.
  penType: PenType;
  // Active shape tool: when set, a pen drag draws this measured shape (line/rect/circle/…)
  // instead of freehand ink; null = freehand.
  shape: ShapeKind | null;
}

/** A shape's polyline point carries no pressure/timing — flat samples keep the outline crisp. */
const toStrokePoint = (p: { x: number; y: number }): StrokePoint => ({ x: p.x, y: p.y, p: 0.5, t: 0 });

/** Paint the ephemeral size readout (a blue pill + white text) for the shape being drawn. */
function drawShapeLabel(ctx: CanvasRenderingContext2D, lbl: { text: string; x: number; y: number }) {
  ctx.save();
  const fs = 16;
  ctx.font = `600 ${fs}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const padX = 7, padY = 4, r = 5;
  const tw = ctx.measureText(lbl.text).width;
  const cx = lbl.x;
  const cy = Math.max(lbl.y, fs);            // keep the pill on-page for shapes near the top edge
  const bw = tw + padX * 2, bh = fs + padY * 2;
  const bx = cx - bw / 2, by = cy - bh / 2;
  ctx.beginPath();
  ctx.moveTo(bx + r, by);
  ctx.arcTo(bx + bw, by, bx + bw, by + bh, r);
  ctx.arcTo(bx + bw, by + bh, bx, by + bh, r);
  ctx.arcTo(bx, by + bh, bx, by, r);
  ctx.arcTo(bx, by, bx + bw, by, r);
  ctx.closePath();
  ctx.fillStyle = 'rgba(37, 99, 235, 0.92)';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(lbl.text, cx, cy);
  ctx.restore();
}

interface Props extends InkTool {
  block: StrokesBlock;
  readOnly?: boolean;
  // Palm rejection: when true, finger/touch input is ignored (only a stylus or mouse draws),
  // and a finger can scroll the page instead.
  penOnly?: boolean;
  // Fired the first time a stylus pointer is seen, so the parent can auto-enable palm rejection.
  onPenDetected?: () => void;
  onChange: (next: StrokesBlock) => void;
}

// A4 portrait aspect (210 × 297 mm). A page's logical height is capped at this multiple of its
// logical width, so the page maps 1:1 onto one printed sheet and the rasterised render fed to
// AI grading is always a single, predictably-sized page. Past the cap the student adds a page.
const A4_RATIO = 297 / 210;

// Canvas backing store scale so strokes stay crisp on HiDPI screens and tablets.
const BACKING_SCALE = Math.min(window.devicePixelRatio || 1, 3);

// Auto-grow within a page: when a point lands within GROW_THRESHOLD of the bottom, extend the
// page by GROW_INCREMENT — but never past the A4 cap. All values are in logical px.
const GROW_THRESHOLD = 80;
const GROW_INCREMENT = 320;

/** Single ink page. Uses Pointer Events, so stylus / touch / mouse all feed in the same way.
 *  Stylus pressure/tilt are captured when reported; touch and mouse fall back to 0.5 pressure.
 *  Strokes are stored in a fixed logical A4 coordinate space (not screen px) and mapped to/from
 *  the rendered size, so a page looks the same on any device and prints/grades consistently.
 *  Pen settings and undo/clear live in the parent InkPad; this component just draws one page. */
function InkCanvasImpl({ block, readOnly, tool, color, width, eraserSize, penType, shape, penOnly, onPenDetected, onChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const drawingRef = useRef<Stroke | null>(null);
  const startedAtRef = useRef<number>(0);
  // Last known eraser cursor position in logical px; null while the pointer is off-canvas.
  // Stored in a ref so cursor moves don't trigger React renders.
  const eraserCursorRef = useRef<{ x: number; y: number } | null>(null);
  // In-flight strokes for the current eraser drag. React 18 batches the onChange calls from
  // successive pointermoves, so without this each move would re-read the original `block`
  // from a stale closure and overwrite the previous move's split.
  const liveStrokesRef = useRef<Stroke[] | null>(null);
  // Active finger-pan (palm-rejection mode): we keep touch-action:none so the stylus always
  // draws (never scrolls), and instead scroll the page ourselves when a finger drags.
  const panRef = useRef<{ y: number; el: HTMLElement | null } | null>(null);
  // Shape tool: the anchor (press point) of the in-progress shape, and the live size readout
  // to paint over it. Both are refs so a shape drag never triggers a React render per move.
  const shapeAnchorRef = useRef<StrokePoint | null>(null);
  const shapeLabelRef = useRef<{ text: string; x: number; y: number } | null>(null);

  // Nearest scrollable ancestor — the element a finger-pan should scroll.
  function scrollAncestor(): HTMLElement | null {
    let el = wrapRef.current?.parentElement ?? null;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  }

  // The paper this page shows. Old pages only carry the legacy `ruled` flag.
  const sheetType = block.sheetType ?? (block.ruled === false ? 'plain' : 'ruled');

  // Fixed logical coordinate space for this page.
  const logicalWidth = block.width || A4_LOGICAL_WIDTH;
  const maxHeight = Math.round(logicalWidth * A4_RATIO);
  const heightLogical = Math.max(PAGE_MIN_HEIGHT, block.height || PAGE_MIN_HEIGHT);

  // Height we've already requested via onChange — guards against firing the same grow call
  // again while the parent re-render is still in flight.
  const requestedHeightRef = useRef<number>(heightLogical);
  if (requestedHeightRef.current < heightLogical) requestedHeightRef.current = heightLogical;

  function maybeGrowFor(y: number) {
    if (readOnly) return;
    const cur = requestedHeightRef.current;
    if (cur >= maxHeight) return;            // A4 cap reached — page no longer grows
    if (y > cur - GROW_THRESHOLD) {
      const next = Math.min(maxHeight, cur + GROW_INCREMENT);
      requestedHeightRef.current = next;
      onChange({ ...block, height: next });
    }
  }

  // --- repaint on block / tool change ------------------------------------------------------
  useEffect(() => { redraw(); });

  function redraw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Map the logical coordinate space onto the backing store (folds in HiDPI scaling).
    const k = canvas.width / logicalWidth;
    ctx.setTransform(k, 0, 0, k, 0, 0);
    for (const s of block.strokes) drawStroke(ctx, s);
    // Draw the stroke that's mid-progress on top.
    if (drawingRef.current) drawStroke(ctx, drawingRef.current);
    // Live measurement readout for an in-progress shape (ephemeral — never saved with the ink).
    if (shapeLabelRef.current && !readOnly) drawShapeLabel(ctx, shapeLabelRef.current);
    // Eraser preview: dashed ring matching the hit radius so the user sees exactly what a
    // click/drag will remove (Paint-style cursor).
    if (tool === 'eraser' && eraserCursorRef.current && !readOnly) {
      const { x, y } = eraserCursorRef.current;
      ctx.save();
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.arc(x, y, eraserSize, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  // --- resize canvas backing store ---------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ro = new ResizeObserver(() => {
      const rect = wrap.getBoundingClientRect();
      const w = rect.width || logicalWidth;
      const h = rect.height || w * (heightLogical / logicalWidth);
      canvas.width = Math.max(1, w * BACKING_SCALE);
      canvas.height = Math.max(1, h * BACKING_SCALE);
      redraw();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // --- pointer handling --------------------------------------------------------------------
  // Logical px per rendered (CSS) px. Uniform because the page keeps its A4 aspect ratio.
  function logicalScale(rect: DOMRect): number {
    return logicalWidth / (rect.width || logicalWidth);
  }

  function pointFromEvent(e: React.PointerEvent): StrokePoint {
    const rect = canvasRef.current!.getBoundingClientRect();
    const k = logicalScale(rect);
    return {
      x: (e.clientX - rect.left) * k,
      y: (e.clientY - rect.top) * k,
      p: e.pressure && e.pressure > 0 ? e.pressure : 0.5,
      t: performance.now() - startedAtRef.current,
      tx: (e as any).tiltX,
      ty: (e as any).tiltY
    };
  }

  function eraseAt(pt: StrokePoint): boolean {
    const center = { x: pt.x, y: pt.y };
    // Clip against the running copy if a drag is in flight, otherwise the props.
    const source = liveStrokesRef.current ?? block.strokes;
    const next: Stroke[] = [];
    let changed = false;
    for (const s of source) {
      const fragments = clipStrokeAgainstCircle(s, center, eraserSize);
      if (fragments.length === 1 && fragments[0] === s) {
        next.push(s);
      } else {
        next.push(...fragments);
        changed = true;
      }
    }
    if (!changed) return false;
    liveStrokesRef.current = next;
    onChange({ ...block, strokes: next });
    return true;
  }

  function handleDown(e: React.PointerEvent) {
    if (readOnly) return;
    // The first stylus contact lets the parent auto-enable palm rejection.
    if (e.pointerType === 'pen') onPenDetected?.();
    // Palm rejection: a finger never draws — it scrolls the page instead. We drive the scroll
    // manually (touch-action stays 'none' so the stylus can't scroll, only write).
    if (penOnly && e.pointerType === 'touch') {
      panRef.current = { y: e.clientY, el: scrollAncestor() };
      (e.target as Element).setPointerCapture(e.pointerId);
      return;
    }
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    startedAtRef.current = performance.now();

    if (tool === 'eraser') {
      const pt = pointFromEvent(e);
      eraserCursorRef.current = { x: pt.x, y: pt.y };
      if (!eraseAt(pt)) redraw();
      return;
    }

    const pointerType = (e.pointerType as 'pen' | 'touch' | 'mouse') || 'mouse';
    const first = pointFromEvent(e);
    const rawY = first.y;
    // Match the clamp applied in handleMove: a touchdown past the bottom of the page shouldn't
    // seed the stroke with an out-of-page y, or the first segment draws a vertical line.
    first.y = Math.max(0, Math.min(rawY, requestedHeightRef.current));

    // Shape tool: anchor a measured shape here. It's rebuilt from (anchor → current) on every
    // move and stays within the page (like a straight-edge — never grows it).
    if (shape) {
      shapeAnchorRef.current = first;
      const g = shapeGeometry(shape, first, first);
      drawingRef.current = {
        id: crypto.randomUUID(), color, width, pointerType, points: g.points.map(toStrokePoint), pen: penType,
      };
      shapeLabelRef.current = { text: g.label, x: g.labelAt.x, y: g.labelAt.y };
      redraw();
      return;
    }

    drawingRef.current = {
      id: crypto.randomUUID(),
      color,
      width,
      pointerType,
      points: [first],
      pen: penType
    };
    // Freehand can grow the page as the pen nears the bottom.
    maybeGrowFor(rawY);
  }

  function handleMove(e: React.PointerEvent) {
    if (readOnly) return;
    // Finger-pan in progress (palm-rejection mode): scroll the page by the drag delta.
    if (panRef.current) {
      const dy = panRef.current.y - e.clientY;
      panRef.current.y = e.clientY;
      if (panRef.current.el) panRef.current.el.scrollTop += dy;
      else window.scrollBy(0, dy);
      return;
    }
    // Palm rejection: a finger never draws or erases.
    if (penOnly && e.pointerType === 'touch') return;
    if (tool === 'eraser') {
      const pt = pointFromEvent(e);
      eraserCursorRef.current = { x: pt.x, y: pt.y };
      const erased = (e.buttons & 1) ? eraseAt(pt) : false;
      // If eraseAt fired onChange, the parent rerender will redraw; otherwise repaint manually
      // so the preview ring follows the cursor.
      if (!erased) redraw();
      return;
    }
    const cur = drawingRef.current;
    if (!cur) return;

    // Shape tool: rebuild the whole polyline from (anchor → current point) each move, and refresh
    // the live size readout. The clamp keeps the shape inside the page bounds.
    if (shape && shapeAnchorRef.current) {
      const pt = pointFromEvent(e);
      pt.y = Math.max(0, Math.min(pt.y, requestedHeightRef.current));
      const g = shapeGeometry(shape, shapeAnchorRef.current, pt);
      cur.points = g.points.map(toStrokePoint);
      shapeLabelRef.current = { text: g.label, x: g.labelAt.x, y: g.labelAt.y };
      redraw();
      return;
    }

    // Coalesced events give us every sample the browser had since last frame — crucial for
    // smooth stylus lines at 120+ Hz.
    const events = (e.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [e.nativeEvent as PointerEvent];
    const rect = canvasRef.current!.getBoundingClientRect();
    const k = logicalScale(rect);
    const canvasH = requestedHeightRef.current;
    let maxRawY = 0;
    for (const pe of events) {
      const rawY = (pe.clientY - rect.top) * k;
      // Pointer capture keeps reporting clientY even when the pen drifts off the bottom of the
      // page. Without clamping, a held-still pen on the edge produces points further and
      // further past `canvasH` and the next segment draws a long vertical tail. Clamp so the
      // stroke just rides the bottom edge instead.
      const y = Math.max(0, Math.min(rawY, canvasH));
      cur.points.push({
        x: (pe.clientX - rect.left) * k,
        y,
        p: pe.pressure && pe.pressure > 0 ? pe.pressure : 0.5,
        t: performance.now() - startedAtRef.current,
        tx: (pe as any).tiltX,
        ty: (pe as any).tiltY
      });
      if (rawY > maxRawY) maxRawY = rawY;
    }
    // Grow check uses the unclamped y so a pen sitting past the edge still triggers growth —
    // but only while the page is below the A4 cap.
    if (canvasH < maxHeight && maxRawY > canvasH - GROW_THRESHOLD) {
      const newHeight = Math.min(maxHeight, canvasH + GROW_INCREMENT);
      requestedHeightRef.current = newHeight;
      // A grow is a layout discontinuity for the in-progress stroke: the wrap height changes,
      // ResizeObserver resizes the backing store, and coalesced events straddling the seam can
      // disagree on rect.top — the visible artifact is a long vertical line. Finalize the
      // current stroke and start a fresh one at the most recent point so the stroke continues
      // seamlessly without a connector across the seam.
      onChange({ ...block, height: newHeight, strokes: [...block.strokes, cur] });
      const last = cur.points[cur.points.length - 1];
      drawingRef.current = {
        id: crypto.randomUUID(),
        color: cur.color,
        width: cur.width,
        pointerType: cur.pointerType,
        points: [last],
        pen: cur.pen,
      };
      // No redraw here — the onChange triggers a parent re-render whose effect repaints.
      return;
    }
    redraw();
  }

  function handleUp() {
    panRef.current = null;
    liveStrokesRef.current = null;
    const cur = drawingRef.current;
    const wasShape = !!shapeAnchorRef.current;
    drawingRef.current = null;
    shapeAnchorRef.current = null;
    shapeLabelRef.current = null;
    if (!cur || cur.points.length < 2) { redraw(); return; }
    // Drop a zero-size shape (a tap with no drag) so a stray click leaves nothing behind.
    if (wasShape) {
      const xs = cur.points.map(p => p.x), ys = cur.points.map(p => p.y);
      const extent = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
      if (extent < 4) { redraw(); return; }
    }
    onChange({ ...block, strokes: [...block.strokes, cur] });
  }

  function handleLeave() {
    handleUp();
    if (eraserCursorRef.current) {
      eraserCursorRef.current = null;
      redraw();
    }
  }

  return (
    <div
      ref={wrapRef}
      className={`ink-canvas-wrap sheet-${sheetType}`}
      // Keep touch-action 'none' while writing so the stylus always draws (never scrolls).
      // Finger scrolling in palm-rejection mode is driven manually in the pointer handlers.
      style={{
        aspectRatio: `${logicalWidth} / ${heightLogical}`,
        touchAction: readOnly ? 'auto' : 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        className="ink-canvas"
        // touch-action 'none' so the stylus always writes; finger scrolling (palm-rejection mode)
        // is handled manually in the pointer handlers.
        style={{ cursor: readOnly ? 'default' : 'crosshair',
                 touchAction: readOnly ? 'auto' : 'none' }}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        onPointerLeave={handleLeave}
      />
    </div>
  );
}

// Re-render a page only when its own data or the active tool changes. With several pages
// mounted at once this keeps drawing on one page from repainting all the others.
export const InkCanvas = memo(InkCanvasImpl);

// Clip a stroke against a circular eraser. Returns 0+ sub-strokes that contain only the
// portions of the original polyline outside the circle, with new points inserted at the circle
// boundary so the visible gap matches the eraser's footprint exactly. Without this (i.e. just
// dropping points whose centers fall inside the circle), sparsely sampled strokes lose whole
// arms whenever a single sample lands inside the eraser.
function clipStrokeAgainstCircle(
  s: Stroke,
  c: { x: number; y: number },
  r: number
): Stroke[] {
  const pts = s.points;
  if (pts.length < 2) return [s];
  const r2 = r * r;
  const inside = pts.map(p => {
    const dx = p.x - c.x, dy = p.y - c.y;
    return dx * dx + dy * dy < r2;
  });
  // Fast path: no point is inside and no segment crosses the circle — stroke is untouched.
  if (!inside.some(v => v)) {
    let crosses = false;
    for (let i = 1; i < pts.length; i++) {
      if (segmentCircleTs(pts[i - 1], pts[i], c, r).length >= 2) { crosses = true; break; }
    }
    if (!crosses) return [s];
  }
  const out: Stroke[] = [];
  let seg: StrokePoint[] = [];
  if (!inside[0]) seg.push(pts[0]);
  for (let i = 1; i < pts.length; i++) {
    const A = pts[i - 1], B = pts[i];
    const aIn = inside[i - 1], bIn = inside[i];
    if (!aIn && !bIn) {
      const ts = segmentCircleTs(A, B, c, r);
      if (ts.length >= 2) {
        const t1 = Math.min(ts[0], ts[1]);
        const t2 = Math.max(ts[0], ts[1]);
        seg.push(lerpPoint(A, B, t1));
        if (seg.length >= 2) out.push({ ...s, id: crypto.randomUUID(), points: seg });
        seg = [lerpPoint(A, B, t2), B];
      } else {
        seg.push(B);
      }
    } else if (aIn && !bIn) {
      const ts = segmentCircleTs(A, B, c, r);
      const tExit = ts.length ? Math.max(...ts) : 0;
      seg = [lerpPoint(A, B, tExit), B];
    } else if (!aIn && bIn) {
      const ts = segmentCircleTs(A, B, c, r);
      const tEnter = ts.length ? Math.min(...ts) : 1;
      seg.push(lerpPoint(A, B, tEnter));
      if (seg.length >= 2) out.push({ ...s, id: crypto.randomUUID(), points: seg });
      seg = [];
    }
    // else: both endpoints inside — segment fully erased.
  }
  if (seg.length >= 2) out.push({ ...s, id: crypto.randomUUID(), points: seg });
  return out;
}

// Solve for the parametric values t in [0,1] where segment A→B crosses the circle.
function segmentCircleTs(
  A: { x: number; y: number },
  B: { x: number; y: number },
  c: { x: number; y: number },
  r: number
): number[] {
  const dx = B.x - A.x, dy = B.y - A.y;
  const ex = A.x - c.x, ey = A.y - c.y;
  const a = dx * dx + dy * dy;
  const b = 2 * (ex * dx + ey * dy);
  const k = ex * ex + ey * ey - r * r;
  if (a < 1e-9) return [];
  const disc = b * b - 4 * a * k;
  if (disc < 0) return [];
  const sq = Math.sqrt(disc);
  const ts: number[] = [];
  const t1 = (-b - sq) / (2 * a);
  const t2 = (-b + sq) / (2 * a);
  if (t1 >= 0 && t1 <= 1) ts.push(t1);
  if (t2 >= 0 && t2 <= 1) ts.push(t2);
  return ts;
}

function lerpPoint(A: StrokePoint, B: StrokePoint, t: number): StrokePoint {
  const tt = Math.max(0, Math.min(1, t));
  return {
    x: A.x + (B.x - A.x) * tt,
    y: A.y + (B.y - A.y) * tt,
    p: A.p + (B.p - A.p) * tt,
    t: A.t + (B.t - A.t) * tt,
    tx: A.tx,
    ty: A.ty,
  };
}
